import React, { createContext, useContext, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Create navigation context
const NavigationContext = createContext();

export const NavigationProvider = ({ children }) => {
  const [isNavigating, setIsNavigating] = useState(false);
  const [navigationHistory, setNavigationHistory] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  // Centralized navigation function with safeguards
  const navigateTo = useCallback((path, options = {}) => {
    // Prevent navigation if already on the same page
    if (location.pathname === path) {
      console.log('Already on page, preventing navigation:', path);
      return false;
    }

    console.log('Navigating to:', path);
    
    // Set navigating state
    setIsNavigating(true);
    
    // Perform navigation
    navigate(path, options);
    
    // Add to history
    setNavigationHistory(prev => [...prev, { path, timestamp: Date.now() }]);
    
    // Clear navigating state after a short delay
    setTimeout(() => {
      setIsNavigating(false);
    }, 300);
    
    return true;
  }, [location, navigate]);

  // Get navigation state
  const getNavigationState = useCallback(() => {
    return {
      isNavigating,
      navigationHistory,
      canNavigate: (path) => !isNavigating && location.pathname !== path
    };
  }, [isNavigating, navigationHistory, location]);

  const value = {
    navigateTo,
    getNavigationState,
    isNavigating,
    navigationHistory
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

// Custom hook to use navigation context
export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};
